// ============================================================
// AttackMacros - Saved attack configuration presets, as the
// cockpit's thin top bar (v8.231.0, where the weapon hotbar
// stood). Click restores a config - including which weapon is
// armed, which is why the bar earns the prime slot.
// ============================================================
import { useState } from 'react';
import styles from '../styles/AttackMacros.module.css';
import common from '../styles/common.module.css';
import type { MacroPreset } from '../types';
import { activatable } from '../utils/keyActivation';

interface AttackMacrosProps {
  macros: MacroPreset[];
  onLoadMacro: (macro: MacroPreset) => void;
  onSaveMacro: (name: string) => void;
  onDeleteMacro: (id: string) => void;
  /**
   * Asks the user for a macro name - the app's own dialog, resolving
   * null on cancel. window.prompt is banned here: it blocks the tab
   * and cannot be styled or tested like the rest of the UI.
   */
  onRequestName: () => Promise<string | null>;
}

export default function AttackMacros({
  macros,
  onLoadMacro,
  onSaveMacro,
  onDeleteMacro,
  onRequestName,
}: AttackMacrosProps) {
  const [ctxMenu, setCtxMenu] = useState<{ id: string; x: number; y: number } | null>(null);

  const handleSave = async () => {
    const name = await onRequestName();
    if (name && name.trim()) {
      onSaveMacro(name.trim());
    }
  };

  return (
    <>
      <div className={styles.bar}>
        {macros.map((m) => (
          <button
            key={m.id}
            className={styles.macroBtn}
            onClick={() => onLoadMacro(m)}
            onContextMenu={(e) => {
              e.preventDefault();
              setCtxMenu({ id: m.id, x: e.clientX, y: e.clientY });
            }}
            title={`Load macro: ${m.name}`}
          >
            {m.name}
          </button>
        ))}
        <button className={styles.saveBtn} onClick={handleSave} title="Save current config as a macro">
          + Save
        </button>
      </div>

      {ctxMenu && (
        <DeleteMenu
          at={ctxMenu}
          onClose={() => setCtxMenu(null)}
          onDelete={() => {
            onDeleteMacro(ctxMenu.id);
            setCtxMenu(null);
          }}
        />
      )}
    </>
  );
}

interface DeleteMenuProps {
  at: { x: number; y: number };
  onClose: () => void;
  onDelete: () => void;
}

/** The right-click menu: Delete Macro; a click anywhere on it closes it. */
function DeleteMenu({ at, onClose, onDelete }: DeleteMenuProps) {
  return (
    <div className={styles.ctxMenu} style={{ left: at.x, top: at.y }} onClick={onClose} role="presentation">
      <div className={common.dropdownItem} style={{ color: 'var(--danger)' }} {...activatable(onDelete)}>
        Delete Macro
      </div>
    </div>
  );
}
